import { useEffect, useState } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

/**
 * Module-level cache: a single auth resolution shared across every hook
 * consumer. This avoids each hook calling getUser()/getSession() on its own
 * and stealing the gotrue lock from the others (which surfaced as
 * "Lock broken by another request with the 'steal' option" errors and left
 * super-admin gates flashing the locked card).
 */
let cachedSession: Session | null = null;
let cachedUser: User | null = null;
let isReady = false;
let inflight: Promise<void> | null = null;
const subscribers = new Set<() => void>();
let authSubscribed = false;

function notify() {
  subscribers.forEach((fn) => {
    try { fn(); } catch { /* ignore */ }
  });
}

function ensureAuthSubscription() {
  if (authSubscribed) return;
  authSubscribed = true;
  supabase.auth.onAuthStateChange((_evt, session) => {
    cachedSession = session ?? null;
    cachedUser = session?.user ?? null;
    isReady = true;
    notify();
  });
}

function ensureBootstrap(): Promise<void> {
  if (isReady) return Promise.resolve();
  if (inflight) return inflight;
  ensureAuthSubscription();
  inflight = supabase.auth
    .getSession()
    .then(({ data }) => {
      cachedSession = data.session ?? null;
      cachedUser = data.session?.user ?? null;
      isReady = true;
      notify();
    })
    .catch(() => {
      isReady = true;
      notify();
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

export interface UseAuthReadyResult {
  user: User | null;
  session: Session | null;
  userId: string | null;
  isReady: boolean;
}

/**
 * Returns the current authenticated user and a flag indicating that the
 * Supabase session has been restored from storage at least once.
 *
 * Components should keep showing a loading state until `isReady` is true,
 * and only treat a missing user as "signed out" once `isReady` resolves.
 */
export function useAuthReady(): UseAuthReadyResult {
  const [, setTick] = useState(0);

  useEffect(() => {
    const rerender = () => setTick((n) => n + 1);
    subscribers.add(rerender);
    void ensureBootstrap();
    return () => {
      subscribers.delete(rerender);
    };
  }, []);

  return {
    user: cachedUser,
    session: cachedSession,
    userId: cachedUser?.id ?? null,
    isReady,
  };
}

export default useAuthReady;
